import React, { useContext, useEffect, useRef, useState } from "react";
import {
  TextField,
  MenuItem,
  IconButton,
  Tooltip,
} from "@mui/material";
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { CommonContext } from "../../../context/CommonContext";
import { AuthContext } from "../../../context/AuthContext";
import { authHttp } from "../../../services/commonHttpService";
import { apisPath } from "../../../utils/path";
import { Formik, FieldArray } from "formik";
import { diagnosticDetailsSchema } from "../../../schema/diagnosticDetailsSchema";
import { diagnosticDetailsInitialValues, mapDiagnosticDetailsResponse } from "../../../schema/diagnosticDetailsInitialValues";

const DiagnosticDetails = ({ patientId, submitForm, formResult, currentStep }) => {
  const { setIsLoading, setSnackState } = useContext(CommonContext);
  const { userId } = useContext(AuthContext);

  const [formValues, setFormValues] = useState(diagnosticDetailsInitialValues);
  const [isFocus, setIsFocus] = useState(false);
  const formRef = useRef(null);
  const fileInputRefs = useRef([]);

  // Trigger submit from parent
  useEffect(() => {
    if (submitForm) {
      formRef.current?.handleSubmit();

      if (Object.keys(formRef.current?.errors || {}).length > 0) {
        setIsFocus(true);
      }

      formResult(false); // parent resets submitForm
    }
  }, [submitForm]);

  // Focus & alert on first error
  useEffect(() => {
    if (isFocus && Object.keys(formRef.current?.errors || {}).length) {
      const keys = Object.keys(formRef.current.errors);
      const firstErrorKey = keys[0];

      // Check if error is in documents array
      if (firstErrorKey.startsWith('documents')) {
        const match = firstErrorKey.match(/documents\[(\d+)\]\.(\w+)/);
        if (match) {
          const [, index, field] = match;
          const element = document.getElementsByName(`documents[${index}].${field}`)[0];
          element?.focus();
          element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      } else {
        document.getElementsByName(firstErrorKey)[0]?.focus();
      }

      // Get the first error message
      const firstError = Object.values(formRef.current.errors)[0];
      const errorMessage = typeof firstError === 'object'
        ? Object.values(firstError)[0]?.document_name || Object.values(firstError)[0]?.documents || 'Please fix validation errors'
        : firstError;

      setSnackState({
        open: true,
        message: errorMessage,
        severity: "error",
      });

      setIsFocus(false);
    }
  }, [isFocus]);

  // Fetch saved diagnostic details
  useEffect(() => {
    if (currentStep === 0 && patientId) {
      fetchStepData();
    }
  }, [currentStep, patientId]);

  const fetchStepData = async () => {
    setIsLoading(true);
    try {
      const response = await authHttp.get(
        `${apisPath?.getPatientTreatmentStep}/${patientId}/1`
      );

      if (response?.data?.data) {
        const mappedValues = mapDiagnosticDetailsResponse(response?.data?.data);
        setFormValues(mappedValues);
      }
    } catch (error) {
      //  If 404 → no previous data (expected)
      if (error?.response?.status === 404) {
        console.log("No previous diagnostic details - starting fresh.");
      } else {
        setSnackState({
          open: true,
          message: error?.response?.data?.message || "Something went wrong",
          severity: "error",
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Handle file selection with validation
  const handleFileChange = async (e, index, setFieldValue, validateField) => {
    const file = e.target.files[0];

    if (file) {
      // Set the field value
      await setFieldValue(`documents[${index}].documents`, file);

      // Trigger validation immediately
      const error = await validateField(`documents[${index}].documents`);

      // If validation fails, show alert and clear the input
      if (error) {
        setSnackState({
          open: true,
          message: error,
          severity: "error",
        });

        // Clear the input
        e.target.value = '';
        setFieldValue(`documents[${index}].documents`, null);
      }
    }
  };

  // Extract filename from path
  const getFilenameFromPath = (path) => {
    if (!path) return '';
    const parts = path.split('/');
    return parts[parts.length - 1];
  };

  // FORM SUBMIT HANDLER
  const submitHandler = async (values) => {
    setIsLoading(true);

    try {
      // Create FormData for multipart upload
      const formData = new FormData();

      // Append basic fields
      formData.append('patient_id', Number(patientId));
      formData.append('user_id', Number(userId));
      formData.append('date_of_diagnosis', values?.date_of_diagnosis || null);
      formData.append('primary_tumour_site', values?.primary_tumour_site.trim());
      formData.append('histology_morphology_description', values?.histology_morphology_description.trim());
      formData.append('icdo3_coding', values?.icdo3_coding.trim());
      formData.append('secondary_tumour_site', values?.secondary_tumour_site.trim());
      formData.append('metastasis_morphology', values?.metastasis_morphology.trim());
      formData.append('is_laterality', Number(values?.is_laterality));
      formData.append('laterality', values?.is_laterality === "1" ? values?.laterality : "");
      formData.append('version', 1);
      formData.append('completed_step', 1);

      // Append documents
      // Only include documents where:
      // 1. Both document_name AND NEW file are provided (new documents)
      // 2. OR document_name exists with existingFilePath but user uploaded NEW file (modified)
      // Skip documents that are unchanged (existingFilePath but no new file)
      const validDocuments = values?.documents.filter(doc => {
        const hasName = doc.document_name && doc.document_name.trim();
        const hasNewFile = doc.documents; // New file uploaded
        const hasExistingFile = doc.existingFilePath;

        // Case 1: New document (name + new file, no existing file)
        if (hasName && hasNewFile && !hasExistingFile) {
          return true;
        }

        // Case 2: Modified document (has existing file but user uploaded new one)
        if (hasName && hasNewFile && hasExistingFile) {
          return true;
        }

        // Skip: Unchanged existing documents (has existing file, no new upload)
        // Skip: Empty rows
        return false;
      });

      // Backend expects separate arrays for document_name, remarks, and files
      validDocuments.forEach((doc) => {
        // Append document names as array
        formData.append('document_name', doc.document_name || '');

        // Append remarks as array
        formData.append('remarks', doc.remarks || '');

        // Append document_id if it exists (for updates)
        if (doc.documentId) {
          formData.append('document_id', doc.documentId);
        }

        // Append files with key 'documents' (multiple files with same key)
        if (doc.documents) {
          formData.append('documents', doc.documents);
        }
      });

      // Debug: Log FormData contents
      for (let pair of formData.entries()) {
        console.log(pair[0] + ': ', pair[1]);
      }

      // Send as multipart/form-data
      await authHttp.post(`${apisPath.patientTreatmentStep}/1`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setSnackState({
        open: true,
        message: "Diagnostic details saved successfully!",
        severity: "success",
      });

      formResult(true); // tell parent step successful
    } catch (err) {
      setSnackState({
        open: true,
        message: err?.response?.data?.message || "Failed to save diagnostic details",
        severity: "error",
      });

      formResult(false);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Formik
      initialValues={formValues}
      enableReinitialize
      validationSchema={diagnosticDetailsSchema}
      onSubmit={submitHandler}
      innerRef={formRef}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        setFieldValue,
        validateField
      }) => (
        <div className="upload-clinical">
          <h6 className="header-sub mb-3">Diagnostic Details</h6>
          <div className="row">
            <div className="col-md-3 col-12 mb-3">
              <label htmlFor="date_of_diagnosis" className="form-label">
                Date of diagnostic
              </label>
              <input
                type="date"
                className={`form-control custom-date ${errors?.date_of_diagnosis && touched?.date_of_diagnosis ? 'is-invalid' : ''
                  }`}
                id="date_of_diagnosis"
                name="date_of_diagnosis"
                value={values?.date_of_diagnosis}
                onChange={handleChange}
                max={new Date().toISOString().split("T")[0]}
                onBlur={handleBlur}
                placeholder="Select date"
              />
              {errors?.date_of_diagnosis && touched?.date_of_diagnosis && (
                <div className="invalid-feedback d-block">
                  {errors?.date_of_diagnosis}
                </div>
              )}
            </div>
          </div>

          {/* Top Row - Cancer Info */}
          <div className="row mb-4">
            <div className="col-md-3 col-12 mb-3">
              <TextField
                fullWidth
                label="Primary Site of Tumour/Topography"
                placeholder="Enter primary site"
                size="small"
                variant="standard"
                name="primary_tumour_site"
                value={values?.primary_tumour_site}
                onChange={handleChange}
                onBlur={handleBlur}
                error={Boolean(errors?.primary_tumour_site && touched?.primary_tumour_site)}
                helperText={touched?.primary_tumour_site && errors?.primary_tumour_site}
              />
            </div>
            <div className="col-md-2 col-12 mb-3">
              <TextField
                fullWidth
                label="Histology/Morphology"
                placeholder="Enter histology"
                size="small"
                variant="standard"
                name="histology_morphology_description"
                value={values?.histology_morphology_description}
                onChange={handleChange}
                onBlur={handleBlur}
                error={Boolean(errors?.histology_morphology_description && touched?.histology_morphology_description)}
                helperText={touched?.histology_morphology_description && errors?.histology_morphology_description}
              />
            </div>
            <div className="col-md-3 col-12 mb-3">
              <TextField
                fullWidth
                label="Coding According to ICD-O-3"
                placeholder="ICD-O-3 code"
                size="small"
                variant="standard"
                name="icdo3_coding"
                value={values?.icdo3_coding}
                onChange={handleChange}
                onBlur={handleBlur}
              // error={Boolean(errors?.icdo3_coding && touched?.icdo3_coding)}
              // helperText={touched?.icdo3_coding && errors?.icdo3_coding}
              // required
              />
            </div>
            <div className="col-md-3 col-12 mb-3">
              <TextField
                fullWidth
                label="Secondary Site of Tumour"
                placeholder="Enter secondary site"
                size="small"
                variant="standard"
                name="secondary_tumour_site"
                value={values?.secondary_tumour_site}
                onChange={handleChange}
                onBlur={handleBlur}
                error={Boolean(errors?.secondary_tumour_site && touched?.secondary_tumour_site)}
                helperText={touched?.secondary_tumour_site && errors?.secondary_tumour_site}
              />
            </div>
          </div>
          <div className="row mb-4 align-items-center">
            {/* Morphology Field */}
            <div className="col-md-3 col-12 mb-3">
              <TextField
                fullWidth
                label="Morphology of Metastasis"
                placeholder="Enter morphology"
                size="small"
                variant="standard"
                name="metastasis_morphology"
                value={values?.metastasis_morphology}
                onChange={handleChange}
                onBlur={handleBlur}
                error={Boolean(errors?.metastasis_morphology && touched?.metastasis_morphology)}
                helperText={touched?.metastasis_morphology && errors?.metastasis_morphology}
              />
            </div>

            {/* Laterality Radio Group */}
            <div className="col-md-4 col-12 mb-3">
              <label className="form-label fw-semibold d-block mb-2">
                Laterality
              </label>
              <div className="d-flex flex-wrap align-items-center gap-3">
                <label className="d-flex align-items-center gap-1">
                  <input
                    type="radio"
                    name="is_laterality"
                    value="1"
                    checked={values?.is_laterality === "1"}
                    onChange={(e) => {
                      setFieldValue("is_laterality", e.target.value);
                      if (e.target.value !== "1") {
                        setFieldValue("laterality", "");
                      }
                    }}
                    onBlur={handleBlur}
                  />
                  Yes
                </label>
                <label className="d-flex align-items-center gap-1">
                  <input
                    type="radio"
                    name="is_laterality"
                    value="0"
                    checked={values?.is_laterality === "0"}
                    onChange={(e) => {
                      setFieldValue("is_laterality", e.target.value);
                      setFieldValue("laterality", "");
                    }}
                    onBlur={handleBlur}
                  />
                  No
                </label>
                <label className="d-flex align-items-center gap-1">
                  <input
                    type="radio"
                    name="is_laterality"
                    value="2"
                    checked={values?.is_laterality === "2"}
                    onChange={(e) => {
                      setFieldValue("is_laterality", e.target.value);
                      setFieldValue("laterality", "");
                    }}
                    onBlur={handleBlur}
                  />
                  Unknown
                </label>
              </div>
              {/* {errors?.is_laterality && touched?.is_laterality && (
                <div className="text-danger small mt-1">
                  {errors?.is_laterality}
                </div>
              )} */}
            </div>

            {/* Dropdown (Right Side, Same Row) */}
            <div className="col-md-3 col-12 mb-3">
              {values?.is_laterality === "1" && (
                <TextField
                  fullWidth
                  select
                  label="Laterality"
                  size="small"
                  variant="standard"
                  name="laterality"
                  value={values?.laterality}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  // error={Boolean(errors?.laterality && touched?.laterality)}
                  // helperText={touched?.laterality && errors?.laterality}
                  className="laterality-dropdown"
                // required
                >
                  <MenuItem value="Right">Right</MenuItem>
                  <MenuItem value="Left">Left</MenuItem>
                  <MenuItem value="Only one side involved, right or left origin unknown">
                    Only one side involved, right or left origin unknown
                  </MenuItem>
                  <MenuItem value="Bilateral involvement, laterality origin unknown">
                    Bilateral involvement, laterality origin unknown
                  </MenuItem>
                  <MenuItem value="Paired site midline tumour">
                    Paired site midline tumour
                  </MenuItem>
                  <MenuItem value="Paired site, laterality unknown">
                    Paired site, laterality unknown
                  </MenuItem>
                </TextField>
              )}
            </div>
          </div>

          {/* Document Upload Section */}
          <div className="row mb-4">
            <div className="col-12">
              <h6 className="header-sub mb-3">Upload Documents</h6>

              <FieldArray name="documents">
                {({ push, remove }) => (
                  <div>
                    {values?.documents?.map((doc, index) => (
                      <div key={index} className="row mb-3 align-items-end border-bottom pb-3">
                        {/* Document Name */}
                        <div className="col-md-3 col-12 mb-2">
                          <TextField
                            fullWidth
                            label="Name of Document"
                            placeholder="Enter document name"
                            size="small"
                            variant="standard"
                            name={`documents[${index}].document_name`}
                            value={doc?.document_name}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={Boolean(
                              errors?.documents?.[index]?.document_name &&
                              touched?.documents?.[index]?.document_name
                            )}
                            helperText={
                              touched?.documents?.[index]?.document_name &&
                              errors?.documents?.[index]?.document_name
                            }
                          />
                        </div>

                        {/* Upload Document */}
                        <div className="col-md-3 col-12 mb-2">
                          <input
                            type="file"
                            accept=".jpg,.jpeg,.pdf"
                            style={{ display: 'none' }}
                            ref={(el) => (fileInputRefs.current[index] = el)}
                            onChange={(e) => handleFileChange(e, index, setFieldValue, validateField)}
                            name={`documents[${index}].documents`}
                            title="Only .jpg, .jpeg, and .pdf files are allowed"
                          />

                          <button
                            type="button"
                            className="btn btn-outline-primary btn-sm w-100"
                            onClick={() => fileInputRefs.current[index]?.click()}
                          >
                            <UploadFileIcon fontSize="small" className="me-1" />
                            {doc?.documents
                              ? doc?.documents.name
                              : doc?.existingFileName
                                ? 'Change File'
                                : 'Upload Document'}
                          </button>

                          {errors?.documents?.[index]?.documents &&
                            touched?.documents?.[index]?.documents && (
                              <div className="text-danger small mt-1">
                                {errors?.documents?.[index]?.documents}
                              </div>
                            )}

                          {doc.existingFilePath && !doc.documents && (
                            <div className="text-success small mt-1">
                              ✓ {getFilenameFromPath(doc.existingFilePath)}
                            </div>
                          )}

                          {doc.documents && (
                            <div className="text-info small mt-1">
                              New: {doc.documents.name}
                            </div>
                          )}
                        </div>

                        {/* Remarks */}
                        <div className="col-md-4 col-12 mb-2">
                          <TextField
                            fullWidth
                            label="Remarks (Optional)"
                            placeholder="Add remarks"
                            size="small"
                            variant="standard"
                            name={`documents[${index}].remarks`}
                            value={doc?.remarks}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                        </div>

                        {/* Delete Button */}
                        <div className="col-md-2 col-12 mb-2 text-end">
                          {values?.documents?.length > 1 && (
                            <Tooltip title="Delete row">
                              <IconButton
                                color="error"
                                onClick={() => remove(index)}
                                size="small"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                        </div>
                      </div>
                    ))}

                    {/* Add New Row Button */}
                    {values?.documents?.length < 10 && (
                      <div className="row">
                        <div className="col-12">
                          <button
                            type="button"
                            className="btn btn-primary btn-sm"
                            onClick={() => push({
                              document_name: "",
                              documents: null,
                              remarks: "",
                            })}
                          >
                            <AddIcon fontSize="small" className="me-1" />
                            Add New Row
                          </button>
                        </div>
                      </div>
                    )}

                    {values?.documents?.length >= 10 && (
                      <div className="alert alert-info mt-2">
                        Maximum 10 documents can be uploaded
                      </div>
                    )}
                  </div>
                )}
              </FieldArray>
            </div>
          </div>

        </div>
      )}
    </Formik>
  );
};

export default DiagnosticDetails;
